import React, { useState, useEffect } from "react";
import api from "../../../../services/api";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Spinner,
} from "reactstrap";

export default ({ isOpen, toggle, clienteId, empresaId, notify, onUpdate }) => {
  const [bensMoveis, setBensMoveis] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBem, setEditingBem] = useState(null);
  const [formData, setFormData] = useState({
    categoria_id: "",
    descricao: "",
    estado_conservacao: "",
  });

  const estadosConservacao = [
    { value: "novo", label: "Novo" },
    { value: "bom", label: "Bom" },
    { value: "regular", label: "Regular" },
    { value: "ruim", label: "Ruim" },
    { value: "inoperante", label: "Inoperante" },
  ];

  useEffect(() => {
    if (isOpen) {
      loadCategorias();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && clienteId && empresaId) {
      loadBensMoveis();
    }
  }, [isOpen, clienteId, empresaId]);

  async function loadBensMoveis() {
    setLoading(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaId}/clientes/${clienteId}/bens-moveis`
      );
      setBensMoveis(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar bens móveis:", error);
      notify("danger", "Erro ao carregar equipamentos");
    } finally {
      setLoading(false);
    }
  }

  async function loadCategorias() {
    try {
      const response = await api.get(`/common/categorias-bens-moveis`);
      setCategorias(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingBem) {
        await api.put(
          `/common/empresas/${empresaId}/clientes/${clienteId}/bens-moveis/${editingBem.id}`,
          formData
        );
        notify("success", "Equipamento atualizado com sucesso");
      } else {
        await api.post(
          `/common/empresas/${empresaId}/clientes/${clienteId}/bens-moveis`,
          formData
        );
        notify("success", "Equipamento cadastrado com sucesso");
      }

      resetForm();
      loadBensMoveis();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao salvar equipamento:", error);
      notify("danger", "Erro ao salvar equipamento");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bem) => {
    setEditingBem(bem);
    setFormData({
      categoria_id: bem.categoria_id || "",
      descricao: bem.descricao || "",
      estado_conservacao: bem.estado_conservacao || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (bem) => {
    if (window.confirm(`Deseja excluir este equipamento?`)) {
      try {
        await api.delete(
          `/common/empresas/${empresaId}/clientes/${clienteId}/bens-moveis/${bem.id}`
        );
        notify("success", "Equipamento excluído com sucesso");
        loadBensMoveis();
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error("Erro ao excluir equipamento:", error);
        notify("danger", "Erro ao excluir equipamento");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      categoria_id: "",
      descricao: "",
      estado_conservacao: "",
    });
    setEditingBem(null);
    setShowForm(false);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const getEstadoBadgeColor = (estado) => {
    switch (estado) {
      case "novo":
        return "success";
      case "bom":
        return "info";
      case "regular":
        return "warning";
      case "ruim":
        return "danger";
      case "inoperante":
        return "dark";
      default:
        return "secondary";
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Parque Atual (Equipamentos)
      </ModalHeader>
      <ModalBody>
        {!showForm ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Lista de Equipamentos</h6>
              <Button
                color="primary"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                <i className="fas fa-plus mr-1" />
                Adicionar Novo
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th>Estado</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {bensMoveis.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        Nenhum equipamento cadastrado
                      </td>
                    </tr>
                  ) : (
                    bensMoveis.map((bem) => (
                      <tr key={bem.id}>
                        <td>
                          {bem.categoria ? (
                            <Badge color="primary" pill>
                              {bem.categoria.nome}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {bem.descricao ? (
                            <span>
                              {bem.descricao.substring(0, 50)}
                              {bem.descricao.length > 50 ? "..." : ""}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Badge
                            color={getEstadoBadgeColor(bem.estado_conservacao)}
                            pill
                          >
                            {estadosConservacao.find(
                              (e) => e.value === bem.estado_conservacao
                            )?.label || bem.estado_conservacao}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleEdit(bem)}
                          >
                            <i className="fas fa-edit" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDelete(bem)}
                          >
                            <i className="fas fa-trash" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="categoria_id">Categoria *</Label>
                  <Input
                    type="select"
                    name="categoria_id"
                    id="categoria_id"
                    value={formData.categoria_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categorias.map((categoria) => (
                      <option key={categoria.id} value={categoria.id}>
                        {categoria.nome}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="estado_conservacao">
                    Estado de Conservação *
                  </Label>
                  <Input
                    type="select"
                    name="estado_conservacao"
                    id="estado_conservacao"
                    value={formData.estado_conservacao}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione o estado</option>
                    {estadosConservacao.map((estado) => (
                      <option key={estado.value} value={estado.value}>
                        {estado.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="descricao">Descrição *</Label>
              <Input
                type="textarea"
                name="descricao"
                id="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                rows="3"
                required
                placeholder="Descreva o equipamento (marca, modelo, ano, etc.)"
              />
            </FormGroup>

            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="secondary"
                className="mr-2"
                onClick={resetForm}
              >
                Cancelar
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving && <Spinner size="sm" className="mr-1" />}
                {editingBem ? "Atualizar" : "Cadastrar"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};
